import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex h-full min-h-0 flex-col bg-[#F5F6F8] p-4 sm:p-6" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-1 items-center justify-center rounded-2xl bg-white"
}
const _hoisted_3 = { class: "text-sm text-[#626468]" }
const _hoisted_4 = {
  key: 1,
  class: "flex min-h-0 flex-1 flex-col gap-5"
}
const _hoisted_5 = { class: "flex justify-end" }
const _hoisted_6 = { class: "w-full sm:w-80 lg:w-64" }
const _hoisted_7 = { class: "space-y-2" }
const _hoisted_8 = { class: "flex items-center gap-3 sm:ml-2 sm:space-x-4" }

import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { UiBadge, UiDatePicker, UiListSearch, UiPanel, UiSortMenuBadge } from '@/components';
import { can } from '@/composables/useAuth';
import { AuraService } from '@/services';
import { toastServiceError } from '@/services/core/notification';
import { useAuraProcessingCostsStore, useUiStore } from '@/stores';
import { Permission } from '@/types';
import { normalizeUtcDateRange, startOfUtcDay } from '@/utils/datetime';
import type { AuraProcessingCost, AuraProcessingCostsFilters, SortMenuItem } from '@/types';

import AuraProcessingCostsHeader from './components/AuraProcessingCostsHeader.vue';
import AuraProcessingCostsFiltersPanel from './components/AuraProcessingCostsFilters.vue';
import AuraProcessingCostsSort from './components/AuraProcessingCostsSort.vue';
import AuraProcessingCostsSummary from './components/AuraProcessingCostsSummary.vue';
import AuraProcessingCostsTable from './components/AuraProcessingCostsTable.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraProcessingCosts',
  setup(__props) {

const items = ref<AuraProcessingCost[]>([]);
const uiStore = useUiStore();
const processingCostsStore = useAuraProcessingCostsStore();
const isLoading = computed(() => uiStore.isLoading);
const total = ref(0);
const totalCost = ref(0);
const totalTokens = ref(0);
const filters = computed({
    get: () => processingCostsStore.filters,
    set: (value: AuraProcessingCostsFilters) => processingCostsStore.setFilters(value),
});
const sortMenuSelected = computed({
    get: () => processingCostsStore.sortMenuSelected,
    set: (value: SortMenuItem[]) => processingCostsStore.setSortMenuSelected(value),
});
const users = ref<Array<{ id: number; name: string }>>([]);
const activatedAt = ref<string | null>(null);
const { t, te } = useI18n();

const canView = computed(() => can(Permission.VIEW_AURA_PROCESSING_COSTS));
const orderBy = computed(() =>
    sortMenuSelected.value.map((item) => `${item.direction}${item.key}`).join(','),
);
const search = computed({
    get: () => filters.value.search || '',
    set: (value: string) => {
        filters.value = { ...filters.value, search: value || undefined, page: 1 };
    },
});
const activatedMinDate = computed(() => {
    if (!activatedAt.value) return undefined;
    return startOfUtcDay(activatedAt.value);
});
const translateProcessingType = (processingType: string): string => {
    const key = `aura.processingCosts.processingTypes.${processingType}`;
    return te(key) ? t(key) : t('aura.processingCosts.processingTypes.unknown');
};
const translateStatus = (status: string): string => {
    const key = `aura.processingCosts.statuses.${status}`;
    return te(key) ? t(key) : t('aura.processingCosts.statuses.unknown');
};
const dateRange = computed<Array<Date>>({
    get: () => [
        new Date(filters.value.dateFrom || Date.now()),
        new Date(filters.value.dateTo || Date.now()),
    ],
    set: (range) => {
        if (range.length < 2) return;
        const [dateFrom, dateTo] = range;
        const [normalizedFrom, normalizedTo] = normalizeUtcDateRange(dateFrom, dateTo);
        filters.value = {
            ...filters.value,
            dateFrom: normalizedFrom.toISOString(),
            dateTo: normalizedTo.toISOString(),
            page: 1,
        };
    },
});
const activeFilters = computed(() => {
    const items: Array<{ field: keyof AuraProcessingCostsFilters; label: string; value: string }> =
        [];
    if (filters.value.search)
        items.push({
            field: 'search',
            label: t('aura.processingCosts.filters.searchLabel'),
            value: filters.value.search,
        });
    if (filters.value.userId) {
        const user = users.value.find(({ id }) => id === filters.value.userId);
        if (user)
            items.push({
                field: 'userId',
                label: t('aura.processingCosts.filters.userLabel'),
                value: user.name,
            });
    }
    if (filters.value.processingType)
        items.push({
            field: 'processingType',
            label: t('aura.processingCosts.filters.typeLabel'),
            value: translateProcessingType(filters.value.processingType),
        });
    if (filters.value.status)
        items.push({
            field: 'status',
            label: t('aura.processingCosts.filters.statusLabel'),
            value: translateStatus(filters.value.status),
        });
    return items;
});

const loadCosts = async () => {
    if (!canView.value) return;

    uiStore.setIsLoading(true);

    try {
        const response = await AuraService.getProcessingCosts({
            ...filters.value,
            sort: orderBy.value,
        });
        items.value = response.data.items;
        total.value = response.data.total;
        totalCost.value = response.data.totalCost;
        totalTokens.value = response.data.totalTokens;
        users.value = response.data.users;
        activatedAt.value = response.data.activatedAt;
    } catch (error) {
        toastServiceError(error);
    } finally {
        uiStore.setIsLoading(false);
    }
};

const formatCost = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 6,
    }).format(value);

const formatDate = (value: string) =>
    new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const removeFilter = (field: keyof AuraProcessingCostsFilters) => {
    filters.value = { ...filters.value, [field]: undefined, page: 1 };
};

const removeSort = (item: SortMenuItem) => {
    sortMenuSelected.value = sortMenuSelected.value.filter(({ key }) => key !== item.key);
};

onMounted(loadCosts);
watch([filters, sortMenuSelected], () => loadCosts(), { deep: true });

return (_ctx: any,_cache: any) => {
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AuraProcessingCostsHeader, {
      "is-loading": isLoading.value,
      onRefresh: loadCosts
    }, null, 8, ["is-loading"]),
    (!canView.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t('aura.processingCosts.accessDenied')), 1)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_unref(UiDatePicker), {
                modelValue: dateRange.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
                range: "",
                multiCalendars: "",
                "min-date": activatedMinDate.value
              }, null, 8, ["modelValue", "min-date"])
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(UiPanel), {
              class: "flex flex-col gap-3 px-4 py-2 sm:flex-row sm:items-center",
              "no-padding": ""
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(UiListSearch), {
                  modelValue: search.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
                  placeholder: _ctx.$t('aura.processingCosts.filters.search')
                }, null, 8, ["modelValue", "placeholder"]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(AuraProcessingCostsSort, {
                    modelValue: sortMenuSelected.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((sortMenuSelected).value = $event))
                  }, null, 8, ["modelValue"]),
                  _createVNode(AuraProcessingCostsFiltersPanel, {
                    modelValue: filters.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters).value = $event)),
                    users: users.value
                  }, null, 8, ["modelValue", "users"])
                ])
              ]),
              _: 1
            }),
            (sortMenuSelected.value.length || activeFilters.value.length)
              ? (_openBlock(), _createBlock(_unref(UiPanel), {
                  key: 0,
                  class: "space-x-2"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortMenuSelected.value, (selected) => {
                      return (_openBlock(), _createBlock(_unref(UiSortMenuBadge), {
                        key: selected.key,
                        item: selected,
                        onRemove: ($event: any) => (removeSort(selected))
                      }, null, 8, ["item", "onRemove"]))
                    }), 128)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
                      return (_openBlock(), _createBlock(_unref(UiBadge), {
                        key: filter.field,
                        removable: "",
                        onRemove: ($event: any) => (removeFilter(filter.field))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(filter.label) + ": " + _toDisplayString(filter.value), 1)
                        ]),
                        _: 2
                      }, 1032, ["onRemove"]))
                    }), 128))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _createVNode(AuraProcessingCostsSummary, {
            "total-cost": formatCost(totalCost.value),
            "total-items": total.value,
            "total-tokens": totalTokens.value.toLocaleString('pt-BR')
          }, null, 8, ["total-cost", "total-items", "total-tokens"]),
          _createVNode(AuraProcessingCostsTable, {
            items: items.value,
            "is-loading": isLoading.value,
            "format-cost": formatCost,
            "format-date": formatDate
          }, null, 8, ["items", "is-loading"]),
          (items.value.length)
            ? (_openBlock(), _createBlock(_component_UiPagination, {
                key: 0,
                modelValue: filters.value.page,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.page) = $event)),
                count: total.value,
                "per-page": filters.value.perPage || 25
              }, null, 8, ["modelValue", "count", "per-page"]))
            : _createCommentVNode("", true)
        ]))
  ]))
}
}

})